import { useHistory } from 'react-router'
import { useTranslation, useTypedSelector } from '../../../../../hooks'
import NavigationArrowLink from '../../../../reusable/buttons/NavigationArrowLink'
import {
    Container,
    BackToHomeScreen,
    PolicyContainer,
    Heading,
    Paragraph,
    NavigationLink,
    NavigationLinkContainer,
    NavigationLinkDivider,
    Li,
} from '../styled'
import styled from 'styled-components'

const Bold = styled.span`
    font-weight: bold;
`

const EnglishPolicy = ({ policyContactLines }: { policyContactLines: any }) => {
    return (
        <PolicyContainer>
            <Heading>1. Purpose</Heading>
            <Paragraph>
                The purpose of this Whistleblowing Policy is to provide
                information on how Klaravik AB's internal whistleblowing system
                operates and how reports of misconduct can be made. The policy
                aims to ensure that serious irregularities are identified and
                addressed in a timely manner. The policy has been established in
                accordance with the Swedish Whistleblower Protection Act
                (2021:890).
            </Paragraph>

            <Heading>2. Introduction</Heading>
            <Paragraph>
                Klaravik AB strives for an open and responsible corporate
                culture in which misconduct is not accepted. As a general
                principle, employees are encouraged to raise concerns with their
                immediate manager or another appropriate company representative
                in the first instance. If this is not possible or appropriate,
                the company's whistleblowing system may be used. The
                whistleblowing system is intended as a supplement to ordinary
                reporting channels.
            </Paragraph>

            <Heading>3. Who can report?</Heading>
            <Paragraph>
                The whistleblowing system may be used by individuals who, in a
                work-related context, have obtained information about Klaravik
                AB, including but not limited to:
            </Paragraph>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                <Li>Employees (regardless of type of employment)</Li>
                <Li>Former employees</Li>
                <Li>Job applicants</Li>
                <Li>Interns and trainees (paid or unpaid)</Li>
                <Li>Volunteers</Li>
                <Li>Self-employed persons and consultants</Li>
                <Li>Shareholders</Li>
                <Li>Members of the Board of Directors and senior executives</Li>
                <Li>
                    Persons working under the management or supervision of
                    suppliers or contractors
                </Li>
            </ul>
            <Paragraph>
                Reports may be submitted regardless of whether the relationship
                with Klaravik AB has ended, is ongoing, or has not yet
                commenced.
            </Paragraph>

            <Heading>4. What can be reported?</Heading>
            <Paragraph>
                The whistleblowing system is intended for reporting misconduct
                that is of public interest. This may include, for example:
            </Paragraph>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                <Li>Criminal activities</Li>
                <Li>Serious breaches of law or other binding regulations</Li>
                <Li>Corruption or other irregularities</Li>
                <Li>
                    Serious deficiencies in environmental, occupational health
                    and safety, or security practices
                </Li>
                <Li>Violations of fundamental rights</Li>
                <Li>Serious breaches of data protection regulations</Li>
                <Li>Serious cases of harassment or sexual harassment</Li>
            </ul>
            <Paragraph>
                Attempts to conceal such misconduct may also be reported.
                Reports must be made in good faith. The system is not intended
                for:
            </Paragraph>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                <Li>Personal conflicts</Li>
                <Li>
                    Dissatisfaction with salary, job duties, or similar matters
                    of a less serious nature
                </Li>
            </ul>
            <Paragraph>
                If there is uncertainty as to whether an issue falls within the
                scope of the system, reporting is encouraged – an initial
                assessment will always be carried out.
            </Paragraph>

            <Heading>5. Protection against retaliation</Heading>
            <Paragraph>
                Individuals who report in accordance with this policy and in
                good faith are protected against retaliation under Swedish law.
                Retaliation refers to any act or omission that occurs as a
                result of the report and that entails or may entail a
                disadvantage to the reporting person, such as dismissal,
                reassignment, changes in salary, or other negative treatment. If
                retaliation occurs, Klaravik AB may be liable for damages in
                accordance with applicable law. Legal consequences are
                ultimately determined by a court of law.
            </Paragraph>

            <Heading>6. How is reporting carried out?</Heading>
            <Paragraph>
                Reports are received by designated and authorized functions
                within Klaravik AB:
            </Paragraph>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                {policyContactLines ? (
                    policyContactLines.map((contact: any) => (
                        <Li key={contact.id}>
                            {contact.first_name} {contact.last_name} –{' '}
                            {contact.contact_information.position}
                        </Li>
                    ))
                ) : (
                    <Li>Line of contacts have not been added</Li>
                )}
            </ul>
            <Paragraph>
                Only authorized persons have access to the reports. Where
                necessary, external parties such as legal counsel or auditors
                may be engaged to conduct investigations. Reports may be
                submitted anonymously or confidentially.
            </Paragraph>

            <Heading>7. Handling of reports</Heading>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                <Li>Receipt of the report is acknowledged within seven days</Li>
                <Li>An initial assessment is carried out</Li>
                <Li>
                    If the report is deemed to fall within the scope of the law,
                    the matter is investigated further
                </Li>
                <Li>
                    The reporting person is provided with feedback within a
                    reasonable time, no later than three months, to the extent
                    permitted by confidentiality requirements
                </Li>
            </ul>
            <Paragraph>
                If a matter is considered particularly serious, it may be
                referred to the competent authority.
            </Paragraph>

            <Heading>8. Communication and supplementary information</Heading>
            <Paragraph>
                Through the system, the reporting person may provide
                supplementary information or respond to questions from the case
                handler. This is voluntary.
            </Paragraph>

            <Heading>9. Confidentiality and personal data</Heading>
            <Paragraph>
                Klaravik AB is the data controller for the processing of
                personal data within the whistleblowing system. Personal data is
                processed only to the extent necessary and in accordance with
                the GDPR and applicable Swedish legislation. The information is
                accessible only to authorized persons. Personal data is not
                retained longer than necessary and is deleted in accordance with
                applicable regulations.
            </Paragraph>

            <Heading>10. External reporting</Heading>
            <Paragraph>
                In addition to internal reporting, reporting persons have the
                right under Swedish law to report to competent authorities. This
                does not affect the protection afforded under the Whistleblower
                Protection Act.
            </Paragraph>

            <Heading>11. Questions</Heading>
            <Paragraph>
                Questions regarding this policy or the whistleblowing system may
                be directed to hr@klaravik.se
            </Paragraph>
        </PolicyContainer>
    )
}

const SwedishPolicy = ({ policyContactLines }: { policyContactLines: any }) => {
    return (
        <PolicyContainer>
            <Heading>1. Syfte</Heading>
            <Paragraph>
                Syftet med denna visselblåsarpolicy är att informera om hur
                Klaravik AB:s interna visselblåsarsystem fungerar och hur
                rapportering av missförhållanden kan ske. Policyn ska bidra till
                att allvarliga missförhållanden uppmärksammas och hanteras i
                tid.
            </Paragraph>
            <Paragraph>
                Policyn är utformad i enlighet med{' '}
                <Bold>
                    {' '}
                    Lag (2021:890) om skydd för personer som rapporterar om
                    missförhållanden.
                </Bold>
            </Paragraph>

            <Heading>2. Inledning</Heading>
            <Paragraph>
                Klaravik AB strävar efter en öppen och ansvarsfull
                företagskultur där missförhållanden inte accepteras. Som
                utgångspunkt uppmuntras medarbetare att i första hand ta upp
                frågor med närmaste chef eller annan lämplig företrädare för
                bolaget. Om detta inte är möjligt eller lämpligt finns möjlighet
                att använda bolagets visselblåsarsystem.
            </Paragraph>
            <Paragraph>
                Visselblåsarsystemet är ett komplement till ordinarie
                rapporteringsvägar.
            </Paragraph>

            <Heading>3. Vem kan rapportera?</Heading>
            <Paragraph>
                Visselblåsarsystemet kan användas av personer som i ett
                arbetsrelaterat sammanhang har fått information om Klaravik AB,
                bland annat:
            </Paragraph>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                <Li>Anställda (oavsett anställningsform)</Li>
                <Li>Tidigare anställda</Li>
                <Li>Arbetssökande</Li>
                <Li>Praktikanter och traineer (betalda eller obetalda)</Li>
                <Li>Volontärer</Li>
                <Li>Egenföretagare och konsulter</Li>
                <Li>Aktieägare</Li>
                <Li>Styrelseledamöter och ledande befattningshavare</Li>
                <Li>
                    Personer som arbetar under ledning eller tillsyn av
                    leverantörer eller entreprenörer
                </Li>
            </ul>
            <Paragraph>
                Rapportering kan ske oavsett om relationen till Klaravik AB är
                avslutad, pågående eller ännu inte påbörjad.
            </Paragraph>

            <Heading>4. Vad kan rapporteras?</Heading>
            <Paragraph>
                Visselblåsarsystemet avser rapportering{' '}
                <Bold>
                    av missförhållanden där det finns ett allmänintresse.
                </Bold>{' '}
                Det kan exempelvis röra sig om:
            </Paragraph>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                <Li>Brottslig verksamhet</Li>
                <Li>
                    Allvarliga överträdelser av lag eller andra bindande regler
                </Li>
                <Li>Korruption eller oegentligheter</Li>
                <Li>
                    Allvarliga brister i miljö-, arbetsmiljö- eller
                    säkerhetsarbete
                </Li>
                <Li>Kränkningar av grundläggande rättigheter</Li>
                <Li>Allvarliga överträdelser av dataskyddsregler</Li>
                <Li>
                    Allvarliga fall av trakasserier eller sexuella trakasserier
                </Li>
            </ul>
            <Paragraph>
                Även försök att dölja sådana missförhållanden kan rapporteras.
            </Paragraph>
            <Paragraph>
                Rapportering ska ske <Bold>i god tro.</Bold> Systemet är inte
                avsett för:
            </Paragraph>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                <Li>Personliga konflikter</Li>
                <Li>
                    Missnöje med lön, arbetsuppgifter eller liknande frågor av
                    mindre allvarlig karaktär
                </Li>
            </ul>
            <Paragraph>
                Vid osäkerhet om ett förhållande omfattas uppmuntras
                rapportering – en inledande bedömning görs alltid.
            </Paragraph>

            <Heading>5. Skydd mot repressalier</Heading>
            <Paragraph>
                Personer som rapporterar i enlighet med denna policy och i god
                tro är skyddade mot repressalier enligt svensk lag.
            </Paragraph>
            <Paragraph>
                Med repressalier avses varje handling eller underlåtenhet som
                sker till följd av rapporteringen och som innebär eller kan
                innebära en nackdel för rapportören, exempelvis uppsägning,
                omplacering, löneförändring eller annan negativ särbehandling.
            </Paragraph>
            <Paragraph>
                Om repressalier skulle förekomma kan Klaravik AB bli
                skadeståndsskyldigt enligt lag. Rättsliga följder prövas ytterst
                av domstol.
            </Paragraph>

            <Heading>6. Hur sker rapportering?</Heading>
            <Paragraph>
                Rapporter tas emot av utsedda och behöriga funktioner inom
                Klaravik AB:
            </Paragraph>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                {policyContactLines ? (
                    policyContactLines.map((contact: any) => (
                        <Li key={contact.id}>
                            {contact.first_name} {contact.last_name} –{' '}
                            {contact.contact_information.position}
                        </Li>
                    ))
                ) : (
                    <Li>Kontaktpersoner har inte lagts till</Li>
                )}
            </ul>
            <Paragraph>
                Endast behöriga personer har tillgång till rapporterna. Vid
                behov kan externa parter, exempelvis jurister eller revisorer,
                anlitas för utredning.
            </Paragraph>
            <Paragraph>
                Rapportering kan ske <Bold>anonymt eller konfidentiellt.</Bold>
            </Paragraph>

            <Heading>7. Handläggning av rapporter</Heading>
            <ul style={{ fontSize: '1.4rem', marginBottom: '1rem' }}>
                <Li>
                    Mottagandet av rapporten bekräftas inom{' '}
                    <Bold>sju dagar</Bold>
                </Li>
                <Li>En inledande bedömning görs</Li>
                <Li>
                    Om rapporten bedöms omfattas av lagen utreds ärendet vidare
                </Li>
                <Li>
                    Rapportören får återkoppling inom skälig tid, senast inom
                    <Bold>tre månader</Bold>, i den mån sekretessregler tillåter
                </Li>
            </ul>
            <Paragraph>
                Om ärendet bedöms särskilt allvarligt kan det överlämnas till
                behörig myndighet.
            </Paragraph>

            <Heading>8. Kommunikation och kompletterande uppgifter</Heading>
            <Paragraph>
                Rapportören kan, via systemet, lämna kompletterande information
                eller svara på frågor från handläggare. Detta är frivilligt.
            </Paragraph>

            <Heading>9. Sekretess och personuppgifter</Heading>
            <Paragraph>
                Klaravik AB är personuppgiftsansvarig för behandlingen av
                personuppgifter inom visselblåsarsystemet.
            </Paragraph>
            <Paragraph>
                Personuppgifter behandlas endast i den utsträckning som är
                nödvändig och i enlighet med <Bold>GDPR</Bold> och tillämplig
                svensk lagstiftning. Uppgifterna är endast tillgängliga för
                behöriga personer.
            </Paragraph>
            <Paragraph>
                Uppgifter sparas inte längre än nödvändigt och gallras enligt
                gällande regelverk.
            </Paragraph>

            <Heading>10. Extern rapportering</Heading>
            <Paragraph>
                Utöver intern rapportering har rapportörer enligt svensk lag
                rätt att rapportera till behöriga myndigheter. Detta påverkar
                inte skyddet enligt visselblåsarlagen.
            </Paragraph>

            <Heading>11. Frågor</Heading>
            <Paragraph>
                Frågor om denna policy eller visselblåsarsystemet kan ställas
                till hr@klaravik.se
            </Paragraph>
        </PolicyContainer>
    )
}

const KlaravikAbPolicy: React.FC = () => {
    const { companyName, organizationalCode, policyContactLines } =
        useTypedSelector((state) => state.whistleblower)

    const history = useHistory()

    const translation = useTranslation()

    const { selectedTranslation } = useTypedSelector(
        (state) => state.translation
    )

    const renderPolicy = () => {
        switch (selectedTranslation) {
            case 'se':
                return <SwedishPolicy policyContactLines={policyContactLines} />
            case 'en':
            default:
                return <EnglishPolicy policyContactLines={policyContactLines} />
        }
    }

    return (
        <>
            <BackToHomeScreen>
                <NavigationArrowLink
                    onClickCallback={() => {
                        history.push(
                            `/organization/${organizationalCode}/whistleblower`
                        )
                    }}
                    linkText={
                        translation.whistleblowerPolicyPage.backToHomescreen
                    }
                />
            </BackToHomeScreen>
            <Container>
                <NavigationLinkContainer>
                    <NavigationLink active={true}>{companyName}</NavigationLink>
                    <NavigationLinkDivider />
                    <NavigationLink active={false}>
                        {selectedTranslation === 'se'
                            ? 'Visselblåsarpolicy'
                            : 'Whistleblower Policy'}
                    </NavigationLink>
                </NavigationLinkContainer>
                {renderPolicy()}
            </Container>
        </>
    )
}

export default KlaravikAbPolicy
